import styles from './MainDisplay.module.css'
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from "react-virtualized-auto-sizer";
import Alert from "../Alert/Alert";
import { useDispatch, useSelector } from "react-redux";
import {
    deselectAlert,
    selectAlert,
    setCriticalAlertsNumber, setEmergencyAlertsNumber, setHistoryAlerts, setHistoryDate, setInfoAlertsNumber, setOtherAlertsNumber,
    setTotalAlertsNumber,
    setWarningAlertsNumber
} from "../../store/reducers/alertReducer";
import AlertGroup from "../AlertGroup/AlertGroup";
import { groupByField, parseDateString, sortList } from "../../utils/utils";
import { alertNameGroups, alertsToGroup, hostNameGroups } from "../../utils/grouping";
import { HISTORY_DISPLAY, SILENCED_DISPLAY } from "../../store/actions/DISPLAY_ACTIONS";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import dayjs from "dayjs";
import AlertService from "../../services/AlertService";
import { setModalError } from "../../store/reducers/modalReducer";
import { useSearchParams } from "react-router-dom";
import { setSilenceRules } from "../../store/reducers/silenceRulesReducer";
import { DEFAULT_SILENCE_DURATION, formatSilenceEnd, parseSilenceDuration } from "../../utils/silenceDuration";

const AlertRow = ({ index, style, data }) => {
    const alert = data.alerts[index]
    if (!alert) {
        return null
    }

    const isSilenceWindowOpen = data.activeSilenceAlertId === alert.alert_id

    return (
        <div style={style}>
            <Alert
                alert={alert}
                isSilenceWindowOpen={isSilenceWindowOpen}
                silenceDuration={isSilenceWindowOpen ? data.silenceDuration : ""}
                silenceDurationError={isSilenceWindowOpen ? data.silenceDurationError : ""}
                silenceDurationHint={isSilenceWindowOpen ? data.silenceDurationHint : ""}
                silenceComment={isSilenceWindowOpen ? data.silenceComment : ""}
                onToggleSilenceWindow={data.onToggleSilenceWindow}
                onSilenceDurationChange={data.onSilenceDurationChange}
                onSilenceCommentChange={data.onSilenceCommentChange}
                onSubmitSilenceAlert={data.onSubmitSilenceAlert}
            />
        </div>
    )
}

export default function MainDisplay({ displayMode }) {
    const dispatch = useDispatch()
    const [searchParams, setSearchParams] = useSearchParams()
    const isActiveSocket = useSelector(state => state.webSocket.isOpened)
    const allAlerts = useSelector(state => state.webSocket.alerts)
    const historyAlertList = useSelector(state => state.setAlertReducer.historyAlerts)
    const isInspectMode = useSelector(state => state.setHeaderMenuItemValue.inspectMode)
    const activeProject = useSelector(state => state.setHeaderMenuItemValue.project)
    const isGrouped = useSelector(state => state.setHeaderMenuItemValue.grouping)
    const foundAlerts = useSelector(state => state.setAlertReducer.foundAlerts)
    const displaySilencedAlerts = useSelector(state => state.setSilenceRules.displaySilencedAlerts)
    const selectedAlerts = useSelector(state => state.setAlertReducer.selectedAlerts)
    const [windowWidth, setWindowWidth] = useState(window.innerWidth)
    const [startDate, setStartDate] = useState(() => parseDateString(searchParams.get("datetime"), new Date()))
    const [sortingFeature, setSortingFeature] = useState(undefined)
    const [sortingDirection, setSortingDirection] = useState(undefined)
    const [activeSilenceAlertId, setActiveSilenceAlertId] = useState(null)
    const [silenceDuration, setSilenceDuration] = useState(DEFAULT_SILENCE_DURATION)
    const [silenceComment, setSilenceComment] = useState("")
    const silenceDurationResult = useMemo(() => parseSilenceDuration(silenceDuration), [silenceDuration])

    const sortedAlerts = useMemo(() => {
        if (!sortingFeature) {
            return allAlerts
        }
        return sortList([...allAlerts], sortingFeature, sortingDirection)
    }, [allAlerts, sortingFeature, sortingDirection])


    const rawAlerts = useMemo(() => {
        switch (displayMode) {
            case SILENCED_DISPLAY:
                return sortedAlerts.filter((alert) => alert.silenced === true)
            case HISTORY_DISPLAY:
                return historyAlertList.filter((alert) => alert.silenced === false)
            default:
                if (displaySilencedAlerts) {
                    return sortedAlerts
                }
                return sortedAlerts.filter((alert) => alert.silenced === false || alert?.customFields?.labels?.display_in_normal === true)
        }
    }, [displayMode, sortedAlerts, historyAlertList, displaySilencedAlerts])

    const alertList = foundAlerts || rawAlerts

    const alertsToDisplay = useMemo(() => {
        if (activeProject === "All") {
            return alertList
        }
        return alertList.filter((alert) => alert.project === activeProject)
    }, [activeProject, alertList])

    const closeSilenceWindow = useCallback(() => {
        setActiveSilenceAlertId(null)
        setSilenceDuration(DEFAULT_SILENCE_DURATION)
        setSilenceComment("")
    }, [])

    const onToggleSilenceWindow = useCallback((alertId) => {
        setActiveSilenceAlertId((prevAlertId) => prevAlertId === alertId ? null : alertId)
        setSilenceDuration(DEFAULT_SILENCE_DURATION)
        setSilenceComment("")
    }, [])

    const onSilenceDurationChange = useCallback((value) => {
        setSilenceDuration(value)
    }, [])

    const onSilenceCommentChange = useCallback((value) => {
        setSilenceComment(value)
    }, [])

    const onSubmitSilenceAlert = useCallback(async (alert) => {
        if (!silenceDurationResult.isValid) {
            dispatch(setModalError(silenceDurationResult.error))
            return
        }

        const silenceRule = {
            project: alert.project,
            host: alert.host,
            alertName: alert.alertName,
            startSilence: Date.now(),
            endSilence: parseSilenceDuration(silenceDuration).endSilence,
            comment: silenceComment
        }

        try {
            await AlertService.safe_silence(silenceRule)
        }
        catch (e) {
            dispatch(setModalError("Oops. Something went wrong. Please, try a bit later"))
        }

        closeSilenceWindow()
    }, [silenceDuration, silenceDurationResult, silenceComment, dispatch, closeSilenceWindow])

    useEffect(() => {
        const historyTimestamp = startDate ? dayjs(startDate).valueOf() : null
        if (historyTimestamp) {
            setSearchParams(prev => {
                const next = new URLSearchParams(prev)
                next.set('datetime', String(Math.floor(historyTimestamp / 1000)))
                return next
            })
        }
        dispatch(setHistoryDate(historyTimestamp))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, dispatch])

    useEffect(() => {
        const fetchSilencedRules = async () => {
            const rules = []
            try {
                const response = await AlertService.getSileneced()
                response.data.forEach((rule) => {
                    rules.push(rule)
                })
            }
            catch (e) {
                dispatch(setModalError("Oops. Something went wrong. Please, try a bit later"))
            }
            dispatch(setSilenceRules(rules))
        }

        fetchSilencedRules()
    }, [dispatch])

    //Track screen width for dynamic scaling for alert height in virtual list
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (displayMode !== HISTORY_DISPLAY) {
            return
        }

        const historySearch = async () => {
            const datetime = Date.parse(startDate) / 1000
            try {
                const response = await AlertService.getHistoryAlerts(datetime)
                const historyAlerts = response.data.history
                dispatch(setHistoryAlerts(historyAlerts))
            }
            catch (e) {
                dispatch(setModalError("Oops. Something went wrong. Please, try a bit later"))
            }
        }

        historySearch()
    }, [displayMode, startDate, dispatch])

    useEffect(() => {
        if (!activeSilenceAlertId) {
            return
        }

        const hasCurrentAlert = alertsToDisplay.some((alert) => alert.alert_id === activeSilenceAlertId)
        if (!hasCurrentAlert) {
            closeSilenceWindow()
        }
    }, [activeSilenceAlertId, alertsToDisplay, closeSilenceWindow])

    useEffect(() => {
        if (!activeSilenceAlertId) {
            return
        }

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                closeSilenceWindow()
            }
        }

        window.addEventListener("keydown", handleEscape)
        return () => {
            window.removeEventListener("keydown", handleEscape)
        }
    }, [activeSilenceAlertId, closeSilenceWindow])

    const rowHeight = useMemo(() => {
        if (windowWidth > 2150 && !isInspectMode) {
            return 65
        }
        if ((1150 < windowWidth && windowWidth <= 2150) && !isInspectMode) {
            return 40
        }
        if (isInspectMode) {
            return 32
        }
        return 47
    }, [windowWidth, isInspectMode])

    const totalAlertsNumber = alertsToDisplay.length
    const emergencyAlertsNumber = alertsToDisplay.filter((alert) => alert.severity.toUpperCase() === 'EMERGENCY').length
    const criticalAlertsNumber = alertsToDisplay.filter((alert) => alert.severity.toUpperCase() === 'CRITICAL').length
    const warningAlertsNumber = alertsToDisplay.filter((alert) => alert.severity.toUpperCase() === 'WARNING').length
    const infoAlertsNumber = alertsToDisplay.filter((alert) => alert.severity.toUpperCase() === 'INFO').length
    const otherAlertsNumber = alertsToDisplay.filter((alert) => alert.severity.toUpperCase() !== 'CRITICAL' && alert.severity.toUpperCase() !== 'WARNING' && alert.severity.toUpperCase() !== 'EMERGENCY' && alert.severity.toUpperCase() !== 'INFO').length

    useEffect(() => {
        dispatch(setTotalAlertsNumber(totalAlertsNumber))
        dispatch(setEmergencyAlertsNumber(emergencyAlertsNumber))
        dispatch(setCriticalAlertsNumber(criticalAlertsNumber))
        dispatch(setWarningAlertsNumber(warningAlertsNumber))
        dispatch(setInfoAlertsNumber(infoAlertsNumber))
        dispatch(setOtherAlertsNumber(otherAlertsNumber))
    }, [dispatch, totalAlertsNumber, emergencyAlertsNumber, criticalAlertsNumber, warningAlertsNumber, infoAlertsNumber, otherAlertsNumber])

    const { ungroupedAlerts, alertGroups } = useMemo(() => {
        const groups = []
        let nextUngroupedAlerts = alertsToDisplay

        if (isGrouped) {
            const groupsByHost = groupByField(alertsToDisplay, 'host')
            const hostnameGroups = hostNameGroups(groupsByHost)
            hostnameGroups.forEach(group => groups.push(group))

            const hostnameAlerts = alertsToGroup(groups)
            nextUngroupedAlerts = alertsToDisplay.filter(alert => !hostnameAlerts.has(alert.alert_id))

            const groupsByAlertName = groupByField(nextUngroupedAlerts, 'alertName')
            const alertnameGroups = alertNameGroups(groupsByAlertName)
            alertnameGroups.forEach(group => groups.push(group))

            const alertnameAlerts = alertsToGroup(groups)
            nextUngroupedAlerts = nextUngroupedAlerts.filter(alert => !alertnameAlerts.has(alert.alert_id))
        }

        return {
            ungroupedAlerts: nextUngroupedAlerts,
            alertGroups: groups
        }
    }, [alertsToDisplay, isGrouped])

    const sortedAlertGroups = useMemo(() => {
        if (sortingFeature === 'severity') {
            return sortList([...alertGroups], sortingFeature, sortingDirection)
        } else {
            return alertGroups
        }

    }, [alertGroups, sortingFeature, sortingDirection])

    const handleSortButton = (feature) => {
        setSortingFeature(feature)
        setSortingDirection((prevDirection) => prevDirection === 'asc' ? 'desc' : 'asc')
    }

    const onCheckBoxClick = useCallback(() => {
        if (selectedAlerts.length === 0) {
            alertsToDisplay.forEach(alert => {
                dispatch(selectAlert(alert.alert_id))
            })
        } else {
            selectedAlerts.forEach(alert => {
                dispatch(deselectAlert(alert))
            })
        }
    }, [selectedAlerts, alertsToDisplay, dispatch])

    const listItemData = useMemo(() => ({
        alerts: ungroupedAlerts,
        activeSilenceAlertId,
        silenceDuration,
        silenceDurationError: silenceDurationResult.error,
        silenceDurationHint: silenceDurationResult.error
            ? silenceDurationResult.error
            : silenceDuration.trim()
                ? `Until ${formatSilenceEnd(parseSilenceDuration(silenceDuration).endSilence)}`
                : 'Use values like 30m, 2h, 1 day, 1 month',
        silenceComment,
        onToggleSilenceWindow,
        onSilenceDurationChange,
        onSilenceCommentChange,
        onSubmitSilenceAlert
    }), [
        ungroupedAlerts,
        activeSilenceAlertId,
        silenceDuration,
        silenceDurationResult.error,
        silenceComment,
        onToggleSilenceWindow,
        onSilenceDurationChange,
        onSilenceCommentChange,
        onSubmitSilenceAlert
    ])

    const itemKey = useCallback((index, data) => data.alerts[index]?.alert_id ?? index, [])

    return (
        <div className={styles.mainDisplay}>
            <div className={`${styles.groupWrapper} ${!isInspectMode ? null : styles.groupWrapper_small}`}>
                <div className={styles.alertsHeader}>
                    <div className={`${isInspectMode ? styles.projectHeader_small : styles.projectHeader}`}>
                        <button
                            className={`${isInspectMode ? styles.filterButton_small_project : styles.filterButton} ${sortingFeature === 'project' ? sortingDirection === 'asc' ? styles.filterButtonAsc : styles.filterButtonDesc : null}`}
                            onClick={(e) => {
                                e.preventDefault()
                                handleSortButton('project')
                            }}
                        >
                            PRJ
                        </button>
                    </div>
                    <div className={`${isInspectMode ? styles.hostHeader_small : styles.hostHeader}`}>
                        <>
                            <label className={styles.checkBoxLabel}>
                                <input
                                    className={`${styles.alertCheckbox} ${!isInspectMode ? styles.alertCheckbox_scaled : null}`}
                                    type="checkbox"
                                    onChange={onCheckBoxClick}
                                    checked={selectedAlerts.length > 0}
                                />
                            </label>
                            <button
                                className={`${isInspectMode ? styles.filterButton_small : styles.filterButton} ${sortingFeature === 'host' ? sortingDirection === 'asc' ? styles.filterButtonAsc : styles.filterButtonDesc : null}`}
                                onClick={(e) => {
                                    e.preventDefault()
                                    handleSortButton('host')
                                }}
                            >
                                Host
                            </button>
                        </>
                    </div>
                    <div className={`${isInspectMode ? styles.severityHeader_small : styles.severityHeader}`}>
                        <button
                            className={`${isInspectMode ? styles.filterButton_small : styles.filterButton} ${sortingFeature === 'severity' ? sortingDirection === 'asc' ? styles.filterButtonAsc : styles.filterButtonDesc : null}`}
                            onClick={(e) => {
                                e.preventDefault()
                                handleSortButton('severity')
                            }}
                        >
                            Severity
                        </button>
                    </div>
                    <div className={`${isInspectMode ? styles.userHeader_small : styles.userHeader}`}>User</div>
                    <div className={`${isInspectMode ? styles.alertHeader_small : styles.alertHeader}`}>
                        <button
                            className={`${isInspectMode ? styles.filterButton_small : styles.filterButton} ${sortingFeature === 'alertName' ? sortingDirection === 'asc' ? styles.filterButtonAsc : styles.filterButtonDesc : null}`}
                            onClick={(e) => {
                                e.preventDefault()
                                handleSortButton('alertName')
                            }}
                        >
                            Alert Name
                        </button>
                    </div>
                    <div className={`${isInspectMode ? styles.timeHeader_small : styles.timeHeader}`}>
                        <button
                            className={`${isInspectMode ? styles.filterButton_small : styles.filterButton} ${sortingFeature === 'fired' ? sortingDirection === 'asc' ? styles.filterButtonAsc : styles.filterButtonDesc : null}`}
                            onClick={(e) => {
                                e.preventDefault()
                                handleSortButton('fired')
                            }}
                        >
                            duration
                        </button>
                    </div>
                    <div className={`${isInspectMode ? styles.messageHeader_small : styles.messageHeader}`}>
                        <button
                            className={`${isInspectMode ? styles.filterButton_small : styles.filterButton} ${sortingFeature === 'msg' ? sortingDirection === 'asc' ? styles.filterButtonAsc : styles.filterButtonDesc : null}`}
                            onClick={(e) => {
                                e.preventDefault()
                                handleSortButton('msg')
                            }}
                        >
                            Alert Message
                        </button>
                    </div>
                    <div
                        className={`${isInspectMode ? styles.controlButtonHeader_small : styles.controlButtonHeader}`}></div>
                    <div
                        className={`${isInspectMode ? styles.controlButtonHeader_small : styles.controlButtonHeader}`}></div>
                    <div
                        className={`${isInspectMode ? styles.controlButtonHeader_small : styles.controlButtonHeader}`}></div>
                </div>

            </div>
            {isActiveSocket ?
                <>
                    {displayMode === HISTORY_DISPLAY
                        ?
                        <div className={styles.dateField}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <MobileDateTimePicker defaultValue={dayjs(startDate)} onChange={e => setStartDate(e)}
                                    ampm={false} />
                            </LocalizationProvider>
                            {/*<button className={styles.submitDateButton} onClick={e => onHistorySearchClick(e)}/>*/}
                        </div>
                        :
                        null
                    }

                    {sortedAlertGroups.length > 0
                        ?
                        sortedAlertGroups.map((group, index) => (
                            <div className={
                                `${styles.groupWrapper} 
                                ${!isInspectMode ? null : styles.groupWrapper_small}
                                ${index === sortedAlertGroups.length - 1 ? isInspectMode ? styles.lastGroupWrapper_small : styles.lastGroupWrapper : ''}`
                            } key={group.id}>
                                <AlertGroup group={group} alertHeight={rowHeight} />
                            </div>
                        ))

                        : <></>
                    }
                    <AutoSizer>
                        {({ height, width }) => (
                            <List
                                className="List"
                                height={height}
                                itemCount={ungroupedAlerts.length}
                                itemSize={rowHeight}
                                itemData={listItemData}
                                itemKey={itemKey}
                                width={width * 0.98}
                            >
                                {AlertRow}
                            </List>
                        )}
                    </AutoSizer>

                </>
                :
                <div style={{ textAlign: 'center', fontSize: '2rem', marginTop: '20px' }}>NO CONNECTION</div>
            }
        </div>
    )
}
